import React from 'react'
import { Search } from 'lucide-react'
import { Card, CardContent } from '../ui'
import { Button } from '../ui/Button'
import styles from '../../styles/components/assets/AssetsInformationSystems.module.scss'
import { useAuth } from '../AuthProvider'
import { useI18n } from '../../i18n'
import { getActiveOrgId } from '../../utils/orgUtils'
import InformationSystemForm, { formValuesToPayload, type InformationSystemFormValues } from './InformationSystemForm'
import InformationSystemPassportPanel, { PassportPanelEmptyState } from './InformationSystemPassportPanel'
import type { PassportSection } from './informationSystemPassportTypes'
import type { InformationSystemPanelContext, ContextNavKind } from './informationSystemPanelContext'
import { drillDownToContext, panelContextFor } from './informationSystemPanelContext'
import type { BulkAssignAssetRow } from './BulkAssignAssetsModal'
import { LIFECYCLE_OPTIONS } from './informationSystemConstants'
import { API_ENDPOINTS } from '../../config/api'
import { downloadCsvExport } from '../../services/informationSystemPhase13Api'
import InformationSystemCompactListRow from './InformationSystemCompactListRow'
import { AuditReadinessProvider } from './AuditReadinessContext'
import type { DrillDownTarget } from './operationalDrillDownContent'
import { fetchOperationalSummaries, type OperationalSummaryRow } from '../../services/informationSystemOperationalApi'
import {
  attachRemediationPriority,
  compareRemediationPriority,
  computeRemediationPriority,
} from '../../utils/informationSystemRemediationRanking'
import { useRestrictedVisibility } from '../../hooks/useRestrictedVisibility'
import { featureFlags } from '../../config/featureFlags'
import {
  fetchInformationSystemsExecutiveSummary,
  type InformationSystemExecutiveSummary,
} from '../../services/enterpriseOperationalUxApi'
import {
  fetchInformationSystems,
  fetchInformationSystem,
  createInformationSystem,
  updateInformationSystem,
  deleteInformationSystem,
  archiveInformationSystem,
  linkAssetToSystem,
  unlinkAssetFromSystem,
  bulkLinkAssets,
  bulkUnlinkAssets,
  isLinkableAssetId,
  type InformationSystemSummary,
  type InformationSystemDetail,
} from '../../services/informationSystemsApi'

interface InformationSystemsTabProps {
  assets: BulkAssignAssetRow[]
  focusSystemId?: string | null
  onFocusHandled?: () => void
  passportSection?: PassportSection
  /** Open archived list when navigated via ?archived=true */
  initialShowArchived?: boolean
  /** Durable URL owner filter: assigned | unassigned */
  ownerFilter?: 'assigned' | 'unassigned' | null
}

function systemHasOwner(s: InformationSystemSummary): boolean {
  if (String(s.businessOwner || '').trim()) return true
  if (String(s.technicalOwner || '').trim()) return true
  if (s.businessOwnerUserId && String(s.businessOwnerUserId).trim()) return true
  if (s.technicalOwnerUserId && String(s.technicalOwnerUserId).trim()) return true
  if (String(s.businessOwnerDisplay || '').trim()) return true
  if (String(s.technicalOwnerDisplay || '').trim()) return true
  return false
}

function mergeOperationalRows(
  systems: InformationSystemSummary[],
  operationalRows: OperationalSummaryRow[]
): OperationalSummaryRow[] {
  const byId = new Map(operationalRows.map((r) => [r.informationSystemId, r]))
  return systems.map((s) => {
    const op = byId.get(s.id)
    if (op) {
      return {
        ...op,
        ownerLabel:
          op.ownerLabel ||
          s.businessOwnerDisplay ||
          s.businessOwner ||
          s.technicalOwnerDisplay ||
          s.technicalOwner ||
          null,
        environment: op.environment ?? s.environment ?? null,
      }
    }
    return {
      informationSystemId: s.id,
      name: s.name,
      code: s.code,
      description: s.description,
      lifecycleStatus: s.lifecycleStatus,
      criticality: s.criticality,
      environment: s.environment,
      ownerLabel: s.businessOwnerDisplay || s.businessOwner || s.technicalOwnerDisplay || s.technicalOwner || null,
      linkedAssetsCount: s.linkedAssetsCount ?? 0,
      applicableStandards: [],
      criticalDataCategories: [],
      readiness: { readinessStatus: 'UNKNOWN', controlCoveragePercent: null },
      topVulnerabilities: [],
      topFailedControls: [],
      topOpenRisks: [],
    }
  })
}

export default function InformationSystemsTab({
  assets,
  focusSystemId,
  onFocusHandled,
  passportSection,
  initialShowArchived = false,
  ownerFilter = null,
}: InformationSystemsTabProps) {
  const { t } = useI18n()
  const { token } = useAuth()
  const orgId = getActiveOrgId()
  const { restrictedMode } = useRestrictedVisibility()

  const [systems, setSystems] = React.useState<InformationSystemSummary[]>([])
  const [operationalRows, setOperationalRows] = React.useState<OperationalSummaryRow[]>([])
  const [executiveBySystemId, setExecutiveBySystemId] = React.useState<
    Record<string, InformationSystemExecutiveSummary>
  >({})
  const [searchQuery, setSearchQuery] = React.useState('')
  const [panelContext, setPanelContext] = React.useState<InformationSystemPanelContext | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [selectedId, setSelectedId] = React.useState<string | null>(null)
  const [detail, setDetail] = React.useState<InformationSystemDetail | null>(null)
  const [mode, setMode] = React.useState<'list' | 'create' | 'edit'>('list')
  const [submitting, setSubmitting] = React.useState(false)
  const [linking, setLinking] = React.useState(false)
  const [formError, setFormError] = React.useState<string | null>(null)
  const [lifecycleFilter, setLifecycleFilter] = React.useState('')
  const [showArchived, setShowArchived] = React.useState(Boolean(initialShowArchived))
  const [archivedCount, setArchivedCount] = React.useState(0)
  const [filterOwnerMissing, setFilterOwnerMissing] = React.useState(false)
  const [filterNoAssets, setFilterNoAssets] = React.useState(false)
  const [filterNoDataCategories, setFilterNoDataCategories] = React.useState(false)
  const [filterNoStandards, setFilterNoStandards] = React.useState(false)
  const [filterNoControls, setFilterNoControls] = React.useState(false)
  const [filterNoEvidence, setFilterNoEvidence] = React.useState(false)
  const [filtersExpanded, setFiltersExpanded] = React.useState(false)
  const [criticalityFilter, setCriticalityFilter] = React.useState('')
  const [problemsOnly, setProblemsOnly] = React.useState(false)
  const [standardFilter, setStandardFilter] = React.useState('')
  const [sortMode, setSortMode] = React.useState<'risk' | 'problems' | 'health' | 'name'>('risk')
  const [visibleCount, setVisibleCount] = React.useState(5)
  const [expandedId, setExpandedId] = React.useState<string | null>(null)
  const PAGE_SIZE = 5

  const loadList = React.useCallback(async () => {
    if (!token) return
    setLoading(true)
    setError(null)
    try {
      const [data, ops, archivedOnly] = await Promise.all([
        fetchInformationSystems(token, orgId, {
          lifecycleStatus: lifecycleFilter || undefined,
          includeArchived: showArchived,
          ownerMissing: filterOwnerMissing,
          noAssets: filterNoAssets,
          noDataCategories: filterNoDataCategories,
          noStandards: filterNoStandards,
          noControls: filterNoControls,
          noEvidence: filterNoEvidence,
        }),
        fetchOperationalSummaries(token, orgId).catch(() => []),
        fetchInformationSystems(token, orgId, {
          lifecycleStatus: 'ARCHIVED',
          includeArchived: true,
        }).catch(() => []),
      ])
      setSystems(Array.isArray(data) ? data : [])
      setArchivedCount(Array.isArray(archivedOnly) ? archivedOnly.length : 0)
      const opRows = Array.isArray(ops) ? ops : []
      setOperationalRows(opRows)
      if (featureFlags.ENTERPRISE_OPERATIONAL_UX_V1 && opRows.length) {
        try {
          const ex = await fetchInformationSystemsExecutiveSummary(
            opRows.map((r) => r.informationSystemId),
          )
          const map: Record<string, InformationSystemExecutiveSummary> = {}
          for (const s of ex.systems || []) {
            map[s.informationSystemId] = s
          }
          setExecutiveBySystemId(map)
        } catch {
          setExecutiveBySystemId({})
        }
      } else {
        setExecutiveBySystemId({})
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('assets.informationSystems.error.load', 'Failed to load information systems'))
    } finally {
      setLoading(false)
    }
  }, [token, orgId, t, lifecycleFilter, showArchived, filterOwnerMissing, filterNoAssets, filterNoDataCategories, filterNoStandards, filterNoControls, filterNoEvidence])

  const loadDetail = React.useCallback(
    async (id: string) => {
      if (!token) return
      try {
        const data = await fetchInformationSystem(token, id, orgId)
        setDetail(data)
        setSelectedId(id)
        setMode('list')
        return data
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : t('assets.informationSystems.error.load', 'Failed to load information systems'))
        return null
      }
    },
    [token, orgId, t]
  )

  const openContext = React.useCallback(
    async (ctx: InformationSystemPanelContext) => {
      setPanelContext(ctx)
      if (selectedId !== ctx.systemId || !detail) {
        await loadDetail(ctx.systemId)
      }
    },
    [loadDetail, selectedId, detail]
  )

  const openSystem = React.useCallback(
    (id: string, kind: ContextNavKind | 'fullPassport' = 'overview') => {
      openContext(panelContextFor(id, kind))
    },
    [openContext]
  )

  React.useEffect(() => {
    if (initialShowArchived) {
      setShowArchived(true)
      setLifecycleFilter('ARCHIVED')
    }
  }, [initialShowArchived])

  React.useEffect(() => {
    if (ownerFilter === 'unassigned') {
      setFilterOwnerMissing(true)
    } else if (ownerFilter === 'assigned') {
      setFilterOwnerMissing(false)
    }
  }, [ownerFilter])

  React.useEffect(() => {
    loadList()
  }, [loadList])

  React.useEffect(() => {
    if (focusSystemId && token) {
      const section = passportSection || 'overview'
      const panelKindMap: Partial<Record<PassportSection, InformationSystemPanelContext['kind']>> = {
        overview: 'overview',
        assets: 'assets',
        data: 'criticalData',
        graph: 'graph',
        readiness: 'readiness',
      }
      const panelKind = panelKindMap[section]
      if (panelKind) {
        openContext({ kind: panelKind, systemId: focusSystemId }).finally(() => onFocusHandled?.())
      } else {
        openContext({ kind: 'fullPassport', systemId: focusSystemId, section }).finally(() => onFocusHandled?.())
      }
    }
  }, [focusSystemId, token, openContext, onFocusHandled, passportSection])

  const systemsForView = React.useMemo(() => {
    if (ownerFilter === 'assigned') {
      return systems.filter((s) => systemHasOwner(s))
    }
    return systems
  }, [systems, ownerFilter])

  const mergedRows = React.useMemo(() => {
    const base = mergeOperationalRows(systemsForView, operationalRows)
    return attachRemediationPriority(base, executiveBySystemId)
  }, [systemsForView, operationalRows, executiveBySystemId])

  const standardOptions = React.useMemo(() => {
    const set = new Set<string>()
    for (const row of mergedRows) {
      for (const s of row.applicableStandards || []) set.add(String(s))
    }
    return [...set].sort()
  }, [mergedRows])

  const filteredRows = React.useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    return mergedRows.filter((row) => {
      if (criticalityFilter && String(row.criticality || '').toUpperCase() !== criticalityFilter) {
        return false
      }
      if (standardFilter) {
        const stds = (row.applicableStandards || []).map((s) => String(s).toUpperCase())
        if (!stds.includes(standardFilter.toUpperCase())) return false
      }
      if (problemsOnly) {
        const p = row.remediationPriority || computeRemediationPriority(row, executiveBySystemId[row.informationSystemId])
        if (!p.problemCount && !p.hasCriticalOrHigh) return false
      }
      if (!q) return true
      const hay = [
        row.name,
        row.code,
        row.description,
        row.environment,
        row.ownerLabel,
        row.informationSystemId,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return hay.includes(q)
    })
  }, [mergedRows, searchQuery, criticalityFilter, standardFilter, problemsOnly, executiveBySystemId])

  const sortedRows = React.useMemo(() => {
    const rows = [...filteredRows]
    if (sortMode === 'name') {
      rows.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
      return rows
    }
    if (sortMode === 'health') {
      rows.sort(
        (a, b) =>
          Number(a.securityHealthPercent ?? 100) - Number(b.securityHealthPercent ?? 100) ||
          String(a.name || '').localeCompare(String(b.name || '')),
      )
      return rows
    }
    if (sortMode === 'problems') {
      rows.sort((a, b) => {
        const pa = a.remediationPriority?.problemCount ?? 0
        const pb = b.remediationPriority?.problemCount ?? 0
        if (pb !== pa) return pb - pa
        return compareRemediationPriority(a, b)
      })
      return rows
    }
    // risk priority (default)
    rows.sort((a, b) => compareRemediationPriority(a, b))
    return rows
  }, [filteredRows, sortMode])

  const listSummary = React.useMemo(() => {
    let withCritHighFix = 0
    let withLowHealth = 0
    for (const row of sortedRows) {
      const p =
        row.remediationPriority ||
        computeRemediationPriority(row, executiveBySystemId[row.informationSystemId])
      if (p.band === 'CRITICAL' || p.band === 'HIGH') withCritHighFix += 1
      const healthPct = Number(row.securityHealthPercent)
      if (Number.isFinite(healthPct) && healthPct < 85) withLowHealth += 1
    }
    return { total: sortedRows.length, withCritHighFix, withLowHealth }
  }, [sortedRows, executiveBySystemId])

  const visibleRows = React.useMemo(
    () => sortedRows.slice(0, Math.max(PAGE_SIZE, visibleCount)),
    [sortedRows, visibleCount],
  )

  React.useEffect(() => {
    setVisibleCount(PAGE_SIZE)
  }, [searchQuery, lifecycleFilter, criticalityFilter, standardFilter, problemsOnly, sortMode, showArchived])

  const handleCreate = async (values: InformationSystemFormValues) => {
    if (!token) return
    setSubmitting(true)
    setFormError(null)
    try {
      await createInformationSystem(token, formValuesToPayload(values), orgId)
      setMode('list')
      await loadList()
    } catch (e: unknown) {
      setFormError(e instanceof Error ? e.message : t('assets.informationSystems.error.save', 'Failed to save'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleUpdate = async (values: InformationSystemFormValues) => {
    if (!token || !selectedId) return
    setSubmitting(true)
    setFormError(null)
    try {
      await updateInformationSystem(token, selectedId, formValuesToPayload(values), orgId)
      setMode('list')
      await loadList()
      await loadDetail(selectedId)
    } catch (e: unknown) {
      setFormError(e instanceof Error ? e.message : t('assets.informationSystems.error.save', 'Failed to save'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (systemId: string) => {
    if (!token) return
    if (!window.confirm(t('assets.informationSystems.confirmDelete', 'Delete this information system?'))) return
    try {
      await deleteInformationSystem(token, systemId, orgId)
      if (selectedId === systemId) {
        setSelectedId(null)
        setDetail(null)
        setPanelContext(null)
      }
      await loadList()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('assets.informationSystems.error.delete', 'Failed to delete'))
    }
  }

  const handleArchive = async (systemId: string) => {
    if (!token) return
    try {
      await archiveInformationSystem(token, systemId, orgId)
      await loadList()
      if (selectedId === systemId) await loadDetail(systemId)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('assets.lifecycle.archiveFailed', 'Failed to archive'))
    }
  }

  const inventoryAssets = assets.filter((a) => isLinkableAssetId(a.id))

  const selectedSummaryRow = React.useMemo(
    () => (selectedId ? mergedRows.find((r) => r.informationSystemId === selectedId) : null),
    [mergedRows, selectedId]
  )

  const activeFilterCount = [
    filterOwnerMissing,
    filterNoAssets,
    filterNoDataCategories,
    filterNoStandards,
    filterNoControls,
    filterNoEvidence,
    problemsOnly,
    Boolean(criticalityFilter),
    Boolean(standardFilter),
  ].filter(Boolean).length

  const refreshDetailAndList = React.useCallback(async () => {
    if (!selectedId) return
    await loadDetail(selectedId)
    await loadList()
  }, [selectedId, loadDetail, loadList])

  if (loading && systems.length === 0) {
    return <p className={styles.loadingText}>{t('common.loading')}</p>
  }

  if (error && systems.length === 0) {
    return (
      <Card>
        <CardContent>
          <p className="text-red-600 text-sm">{error}</p>
          <Button type="button" className="mt-2" onClick={() => loadList()}>
            {t('common.retry', 'Retry')}
          </Button>
        </CardContent>
      </Card>
    )
  }

  if (mode === 'create') {
    return (
      <div className={styles.informationSystemsWorkspace}>
        <InformationSystemForm
          submitting={submitting}
          error={formError}
          onSubmit={handleCreate}
          onCancel={() => setMode('list')}
        />
      </div>
    )
  }

  if (mode === 'edit' && detail) {
    return (
      <div className={styles.informationSystemsWorkspace}>
        <InformationSystemForm
          initial={detail}
          submitting={submitting}
          error={formError}
          onSubmit={handleUpdate}
          onCancel={() => setMode('list')}
        />
      </div>
    )
  }

  const passportPanelProps =
    detail && selectedId && panelContext ? {
    system: detail,
    summaryRow: selectedSummaryRow,
    executiveSummary: executiveBySystemId[selectedId] ?? null,
    panelContext,
    onContextChange: setPanelContext,
    inventoryAssets,
    onDataChanged: refreshDetailAndList,
    onClose: () => {
      setSelectedId(null)
      setDetail(null)
      setPanelContext(null)
    },
    onEdit: () => setMode('edit'),
    onDelete: () => handleDelete(selectedId),
    onArchive: () => handleArchive(selectedId),
    onLifecycleChange: async (status: string) => {
      if (!token) return
      await updateInformationSystem(token, selectedId, { lifecycleStatus: status }, orgId)
      await refreshDetailAndList()
    },
    linking,
    onLinkAsset: async (assetId: string) => {
      if (!token) return
      setLinking(true)
      try {
        await linkAssetToSystem(token, selectedId, assetId, orgId)
        await refreshDetailAndList()
      } finally {
        setLinking(false)
      }
    },
    onUnlinkAsset: async (assetId: string) => {
      if (!token) return
      setLinking(true)
      try {
        await unlinkAssetFromSystem(token, selectedId, assetId, orgId)
        await refreshDetailAndList()
      } finally {
        setLinking(false)
      }
    },
    onBulkLink: async (ids: string[]) => {
      if (!token) return { errors: [] }
      const result = await bulkLinkAssets(token, selectedId, ids, orgId)
      await refreshDetailAndList()
      return result
    },
    onBulkUnlink: async (ids: string[]) => {
      if (!token) return { errors: [] }
      const result = await bulkUnlinkAssets(token, selectedId, ids, orgId)
      await refreshDetailAndList()
      return result
    },
  } : null

  const filterChips = (
    <>
      <label className={styles.chipToggle}>
        <input type="checkbox" checked={filterOwnerMissing} onChange={(e) => setFilterOwnerMissing(e.target.checked)} />
        <span>{t('assets.filters.ownerMissing', 'Owner missing')}</span>
      </label>
      <label className={styles.chipToggle}>
        <input type="checkbox" checked={filterNoAssets} onChange={(e) => setFilterNoAssets(e.target.checked)} />
        <span>{t('assets.filters.noAssets', 'No assets')}</span>
      </label>
      <label className={styles.chipToggle}>
        <input type="checkbox" checked={filterNoDataCategories} onChange={(e) => setFilterNoDataCategories(e.target.checked)} />
        <span>{t('assets.filters.noDataCategories', 'No data categories')}</span>
      </label>
      <label className={styles.chipToggle}>
        <input type="checkbox" checked={filterNoStandards} onChange={(e) => setFilterNoStandards(e.target.checked)} />
        <span>{t('assets.filters.noStandards', 'No standards')}</span>
      </label>
      <label className={styles.chipToggle}>
        <input type="checkbox" checked={filterNoControls} onChange={(e) => setFilterNoControls(e.target.checked)} />
        <span>{t('assets.filters.noControls', 'No controls')}</span>
      </label>
      <label className={styles.chipToggle}>
        <input type="checkbox" checked={filterNoEvidence} onChange={(e) => setFilterNoEvidence(e.target.checked)} />
        <span>{t('assets.filters.noEvidence', 'No evidence')}</span>
      </label>
      <label className={styles.chipToggle}>
        <input type="checkbox" checked={problemsOnly} onChange={(e) => setProblemsOnly(e.target.checked)} />
        <span>{t('assets.filters.hasProblems', 'Has problems')}</span>
      </label>
    </>
  )

  return (
    <AuditReadinessProvider token={token}>
    <div className={styles.informationSystemsWorkspace}>
      {featureFlags.STANDARD_SCOPE_VISIBILITY_V1 && restrictedMode ? (
        <p className="text-xs text-slate-600 mb-2 px-1">
          {t(
            'standardGovernance.showingScopedSystems',
            'Showing systems within your compliance scope.',
          )}
        </p>
      ) : null}
      <div className={styles.systemsToolbar}>
        <div className={styles.toolbarLeft}>
          <div className={styles.toolbarPrimaryRow}>
          <label className={styles.searchWrap}>
            <Search className={styles.searchIcon} size={16} aria-hidden />
            <input
              type="search"
              className={styles.searchField}
              placeholder={t(
                'assets.informationSystems.searchPlaceholder',
                'Search by name, owner, or ID…',
              )}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label={t(
                'assets.informationSystems.searchPlaceholder',
                'Search by name, owner, or ID',
              )}
            />
          </label>
          <select
            className={styles.selectField}
            value={lifecycleFilter}
            onChange={(e) => setLifecycleFilter(e.target.value)}
            aria-label={t('assets.lifecycle.status', 'Lifecycle')}
          >
            <option value="">{t('assets.lifecycle.allStatuses', 'All statuses')}</option>
            {LIFECYCLE_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {t(`assets.lifecycle.${s}`, s)}
              </option>
            ))}
          </select>
          <select
            className={styles.selectField}
            value={criticalityFilter}
            onChange={(e) => setCriticalityFilter(e.target.value)}
            aria-label={t('assets.criticality', 'Criticality')}
          >
            <option value="">{t('assets.informationSystems.allCriticality', 'All criticality')}</option>
            {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <select
            className={styles.selectField}
            value={standardFilter}
            onChange={(e) => setStandardFilter(e.target.value)}
            aria-label={t('assets.postureRow.standards', 'Standards')}
          >
            <option value="">{t('assets.informationSystems.allStandards', 'All standards')}</option>
            {standardOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <select
            className={styles.selectField}
            value={sortMode}
            onChange={(e) => setSortMode(e.target.value as typeof sortMode)}
            aria-label={t('assets.informationSystems.sortBy', 'Sort by')}
          >
            <option value="risk">{t('assets.informationSystems.sortRisk', 'Risk priority')}</option>
            <option value="problems">{t('assets.informationSystems.sortProblems', 'Problems')}</option>
            <option value="health">{t('assets.informationSystems.sortHealth', 'Security Health')}</option>
            <option value="name">{t('assets.informationSystems.sortName', 'Name')}</option>
          </select>
            <button
              type="button"
              className={`${styles.filterToggleBtn} ${filtersExpanded || activeFilterCount > 0 ? styles.filterToggleBtnActive : ''}`.trim()}
              onClick={() => setFiltersExpanded((v) => !v)}
              aria-expanded={filtersExpanded}
            >
              {t('assets.informationSystems.filters', 'Filters')}
              {activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
            </button>
            <label className={styles.archivedSwitch}>
              <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
              <span>{t('assets.lifecycle.showArchived', 'Show archived')}</span>
            </label>
            <button
              type="button"
              className={styles.toolbarBtnSecondary}
              data-testid="archived-systems-button"
              onClick={() => {
                setShowArchived(true)
                setLifecycleFilter('ARCHIVED')
              }}
              title={t('assets.lifecycle.archivedSystemsHint', 'View archived information systems (excluded from active posture)')}
            >
              {t('assets.lifecycle.archivedSystems', 'Archived systems')}
              {archivedCount > 0 ? ` (${archivedCount})` : ''}
            </button>
          </div>
          {filtersExpanded ? <div className={styles.filterChipsRow}>{filterChips}</div> : null}
        </div>
        <div className={styles.toolbarRight}>
          <button
            type="button"
            className={styles.toolbarBtnSecondary}
            onClick={async () => {
              if (!token) return
              const csv = await downloadCsvExport(token, API_ENDPOINTS.INFORMATION_SYSTEMS_EXPORT_CSV)
              const blob = new Blob([csv], { type: 'text/csv' })
              const a = document.createElement('a')
              a.href = URL.createObjectURL(blob)
              a.download = 'information-systems.csv'
              a.click()
            }}
          >
            {t('assets.export.systems', 'Export')}
          </button>
          <button type="button" className={styles.toolbarBtnPrimary} onClick={() => { setMode('create'); setFormError(null) }}>
            {t('assets.informationSystems.addShort', 'Add IS')}
          </button>
        </div>
      </div>

      {error ? <p className={styles.errorBanner}>{error}</p> : null}

      {sortedRows.length > 0 ? (
        <div className={styles.isListSummary} data-testid="is-list-summary">
          <span>
            {t('assets.informationSystems.totalCount', '{count} information systems', {
              count: String(listSummary.total),
            })}
          </span>
          <span>
            {t(
              'assets.informationSystems.critHighFixCount',
              '{count} Critical/High fix priority',
              { count: String(listSummary.withCritHighFix) },
            )}
          </span>
          <span>
            {t(
              'assets.informationSystems.belowGoodHealthCount',
              '{count} below Good Security Health (<85%)',
              { count: String(listSummary.withLowHealth) },
            )}
          </span>
          <span className={styles.isListSummaryHint}>
            {t(
              'assets.informationSystems.sortedByRiskHint',
              'Fix priority = max confirmed problem severity (escalation only with evidence). Business criticality and exposure sort within a band. Security Health uses the Home TSH formula — separate from Fix priority and Risk Engine.',
            )}
          </span>
        </div>
      ) : null}

      {sortedRows.length === 0 ? (
        <Card>
          <CardContent>
            <p className={styles.emptyHint}>
              {searchQuery
                ? t('assets.informationSystems.noSearchResults', 'No systems match your search')
                : t('assets.informationSystems.empty', 'No information systems created yet')}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className={styles.systemsContentGrid}>
          <div className={styles.systemsListScroll}>
          <div className={`${styles.systemsList} ${styles.prioritizedSystemsList}`} data-testid="is-prioritized-list">
            {visibleRows.map((row) => (
              <InformationSystemCompactListRow
                key={row.informationSystemId}
                row={row}
                executiveSummary={executiveBySystemId[row.informationSystemId] ?? null}
                selected={selectedId === row.informationSystemId}
                expanded={expandedId === row.informationSystemId}
                onToggleExpand={() =>
                  setExpandedId((cur) =>
                    cur === row.informationSystemId ? null : row.informationSystemId,
                  )
                }
                onSelect={() => openSystem(row.informationSystemId, 'overview')}
                onOpenPassport={() => openSystem(row.informationSystemId, 'fullPassport')}
                onOpenGraph={() => openSystem(row.informationSystemId, 'graph')}
                onOpenEvidence={() => openSystem(row.informationSystemId, 'evidence')}
                onOpenRemediation={() => openSystem(row.informationSystemId, 'remediation')}
                onOpenControls={() => openSystem(row.informationSystemId, 'controls')}
                onOpenReadiness={() => openSystem(row.informationSystemId, 'readiness')}
                onOpenCriticalData={() => openSystem(row.informationSystemId, 'criticalData')}
                onOpenStandards={() => openSystem(row.informationSystemId, 'standards')}
                onOpenExecutive={() => openSystem(row.informationSystemId, 'executive')}
                onDrillDown={(target) => {
                  if (!target) return
                  openContext(
                    drillDownToContext({
                      kind: target.kind,
                      systemId: target.systemId || row.informationSystemId,
                      id: target.kind === 'risk' ? target.sourceId : target.id || undefined,
                      label: target.label,
                      sourceType: target.kind === 'risk' ? target.sourceType : undefined,
                      sourceId: target.kind === 'risk' ? target.sourceId : undefined,
                    })
                  )
                }}
                onEdit={() => {
                  loadDetail(row.informationSystemId).then(() => setMode('edit'))
                }}
                onArchive={() => handleArchive(row.informationSystemId)}
                onDelete={() => handleDelete(row.informationSystemId)}
              />
            ))}
            {visibleCount < sortedRows.length ? (
              <div className={styles.showMoreWrap}>
                <button
                  type="button"
                  className={styles.toolbarBtnSecondary}
                  data-testid="is-show-more"
                  onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
                >
                  {t('assets.informationSystems.showMore', 'Show more')} (
                  {Math.min(PAGE_SIZE, sortedRows.length - visibleCount)} /{' '}
                  {sortedRows.length - visibleCount}{' '}
                  {t('assets.informationSystems.remaining', 'remaining')})
                </button>
              </div>
            ) : null}
            {passportPanelProps ? (
              <div className={styles.mobilePassportBelow}>
                <InformationSystemPassportPanel {...passportPanelProps} />
              </div>
            ) : null}
          </div>
          </div>

          <div className={styles.desktopPanelColumn}>
            {passportPanelProps ? (
              <InformationSystemPassportPanel {...passportPanelProps} />
            ) : (
              <PassportPanelEmptyState />
            )}
          </div>
        </div>
      )}

    </div>
    </AuditReadinessProvider>
  )
}
